/**
 * Компонент MagneticButton
 * Кнопка с magnetic эффектом (следует за курсором) в стиле 21st.dev
 * Автор: AI Market Hub
 * 
 * Принцип: Замена CartoonButton с сохранением всех props и обратной совместимостью
 */

import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useState, useRef } from 'react';

export default function MagneticButton({
  children,
  onClick,
  variant = 'primary',
  className = '',
  disabled = false,
  magnetic = true,
  ripple = true,
  as,
  href,
  ...props
}) {
  const Component = as || 'button';
  const [ripples, setRipples] = useState([]);
  const ref = useRef(null);

  // Magnetic эффект
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springConfig = { damping: 15, stiffness: 150 };
  const xSpring = useSpring(x, springConfig);
  const ySpring = useSpring(y, springConfig);

  // Преобразуем для движения кнопки
  const translateX = useTransform(xSpring, [-0.5, 0.5], [-8, 8]);
  const translateY = useTransform(ySpring, [-0.5, 0.5], [-8, 8]);

  const handleMouseMove = (e) => {
    if (!magnetic || disabled || !ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const distanceX = (e.clientX - centerX) / rect.width;
    const distanceY = (e.clientY - centerY) / rect.height;
    
    x.set(distanceX);
    y.set(distanceY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const handleClick = (e) => {
    if (disabled) return;

    // Ripple эффект
    if (ripple && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const newRipple = { x, y, id: Date.now() };
      setRipples(prev => [...prev, newRipple]);
      
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }

    onClick?.(e);
  };

  // Варианты стилей (совместимость с CartoonButton)
  const variants = {
    primary: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50',
    secondary: 'bg-white/10 text-white border border-white/20 hover:bg-white/20',
    success: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/30',
    warning: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/30',
    danger: 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/30',
    outline: 'bg-transparent border-2 border-purple-500 text-purple-400 hover:bg-purple-500/20',
    ghost: 'bg-transparent text-gray-300 hover:bg-white/10',
  };

  return (
    <motion.div
      as={Component}
      ref={ref}
      style={{
        x: magnetic ? translateX : 0,
        y: magnetic ? translateY : 0,
      }}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      href={href}
      disabled={disabled}
      className={`
        relative overflow-hidden px-6 py-3 rounded-xl font-semibold
        transition-all duration-300 inline-block text-center
        ${variants[variant] || variants.primary}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      {...props}
    >
      {/* Анимированный градиентный фон */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-[length:200%_100%] opacity-0 group-hover:opacity-100"
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Ripple эффекты */}
      {ripples.map(ripple => (
        <motion.span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 pointer-events-none"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
          }}
          initial={{ scale: 0, opacity: 1 }}
          animate={{ scale: 4, opacity: 0 }}
          transition={{ duration: 0.6 }}
        />
      ))}

      {/* Контент */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
    </motion.div>
  );
}

